// Team workweek rules shared by the server and the client bundle (architecture sprint-05 §3.4).
// Dependency-free (shared-purity.test.ts). ISO weekdays: 1 = Mon … 7 = Sun.

export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
/** Index = ISO weekday − 1. */
export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export const DEFAULT_WORKWEEK = [1, 2, 3, 4, 5] as const;

const MIN_RUN_FOR_RANGE = 3;
const DAYS_PER_WEEK = 7;
const EVERY_DAY = 'Every day';

/** Integers 1..7, no duplicates, 1..7 items. */
export function isValidWorkweek(days: readonly number[]): boolean {
  if (days.length < 1 || days.length > DAYS_PER_WEEK) return false;
  if (!days.every((d) => Number.isInteger(d) && d >= 1 && d <= DAYS_PER_WEEK)) return false;
  return new Set(days).size === days.length;
}

/** Sorted ascending copy (input assumed valid). */
export function normalizeWorkweek(days: readonly number[]): number[] {
  return [...days].sort((a, b) => a - b);
}

function shortName(iso: number): string {
  return WEEKDAY_SHORT[iso - 1] ?? String(iso);
}

/**
 * [1,2,3,4,5] → 'Mon–Fri'; consecutive runs of ≥ 3 become 'A–B'; others comma-joined:
 * [1,3,5] → 'Mon, Wed, Fri'; [1,2,4] → 'Mon, Tue, Thu'; all 7 → 'Every day'.
 */
export function formatWorkweek(days: readonly number[]): string {
  const sorted = normalizeWorkweek(days);
  if (sorted.length === DAYS_PER_WEEK) return EVERY_DAY;

  const runs: number[][] = [];
  for (const day of sorted) {
    const last = runs[runs.length - 1];
    const prev = last?.[last.length - 1];
    if (last && prev !== undefined && day === prev + 1) last.push(day);
    else runs.push([day]);
  }

  const parts: string[] = [];
  for (const run of runs) {
    const first = run[0];
    const end = run[run.length - 1];
    if (first === undefined || end === undefined) continue;
    if (run.length >= MIN_RUN_FOR_RANGE) parts.push(`${shortName(first)}–${shortName(end)}`);
    else parts.push(...run.map(shortName));
  }
  return parts.join(', ');
}
